import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, animate, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/images";
import Reveal from "@/components/Reveal";
import { ParallaxImage, WordReveal, EASE } from "@/components/Fx";
import CorridorMap from "@/components/CorridorMap";

function KineticHero() {
  const { t } = useLang();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    if (v.readyState >= 2) tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-night"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="h-full w-full object-cover"
          data-testid="hero-video"
        >
          <source src="/hero-video.webm" type="video/webm" />
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-night/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/30 to-night/45" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-44 lg:px-10 lg:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold md:text-xs"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-[2px] w-8 bg-terra" />
          {t.home.eyebrow}
        </motion.p>

        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-sand sm:text-6xl md:text-6xl">
          {t.home.titleLines.map((line, i) => (
            <span key={i} className="-mb-2 block overflow-hidden pb-2">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, ease: EASE, delay: 0.35 + i * 0.16 }}
                data-testid={`hero-title-line-${i + 1}`}
              >
                {i === 1 ? highlightCountry(line) : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-sand/80 md:text-lg"
          data-testid="hero-sub"
        >
          {t.home.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/servizi"
            data-testid="hero-cta-services"
            className="group inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:bg-terra-dark"
          >
            {t.home.ctaPrimary}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
          <Link
            to="/contatti"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-2 rounded-full border border-sand/35 px-7 py-3.5 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
          >
            {t.home.ctaSecondary}
          </Link>
        </motion.div>
      </div>

    </section>
  );
}

/* Hero title colors (works for IT/EN/FR): connective words white, the
   country word red; the rest keeps the line's gold italic style. */
const CONNECTIVES = new Set(["fra", "e", "between", "and", "entre", "et", "le", "la"]);

const highlightCountry = (text) =>
  text.split(/(\s+)/).map((part, i) => {
    if (CONNECTIVES.has(part)) {
      return (
        <span key={i} className="text-sand">
          {part}
        </span>
      );
    }
    if (/^(Marocco|Morocco|Maroc)$/.test(part)) {
      return (
        <span key={i} className="text-terra">
          {part}
        </span>
      );
    }
    return part;
  });

function Stats() {
  const { t } = useLang();
  const stats = [...t.home.stats].sort((a, b) => Number(a.n) - Number(b.n));
  return (
    <section className="border-b border-linec bg-sand" data-testid="stats-band">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-12 px-5 py-16 md:grid-cols-4 lg:px-10 lg:py-20">
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="border-l border-linec pl-5" data-testid={`stat-item-${i + 1}`}>
              <motion.span
                initial={{ scale: 0.5, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ type: "spring", stiffness: 210, damping: 15, delay: 0.1 + i * 0.08 }}
                className="inline-block font-serif text-5xl font-medium text-terra md:text-6xl"
              >
                {s.n}
              </motion.span>
              <p className="mt-3 max-w-[180px] text-xs leading-relaxed text-mutedx">
                <span className="font-bold text-terra">{s.kw}</span>
                {s.l.slice((s.kw || "").length)}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Counter({ n, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, n, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, n]);

  return (
    <span ref={ref} data-testid="counter-value">
      {val}
      <span className="text-gold">{suffix}</span>
    </span>
  );
}

function DataBand() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden border-t border-white/10 bg-night" data-testid="data-band">
      <img
        src={IMAGES.zellige}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.09]"
        data-testid="data-band-bg"
      />
      <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-28">
        <Reveal>
          <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
            <span className="inline-block h-[2px] w-8 bg-terra" />
            {t.home.data.eyebrow}
          </p>
          <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight text-sand sm:text-4xl">
            <WordReveal text={t.home.data.title} />
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-sand/65 md:text-lg">
            {t.home.data.text}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {t.home.data.items.map((d, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div
                className="border-l-2 pl-5"
                style={{ borderColor: i % 2 === 0 ? "#187848" : "#D80000" }}
                data-testid={`data-item-${i + 1}`}
              >
                <span className="font-serif text-4xl font-medium text-sand md:text-5xl">
                  <Counter n={d.n} suffix={d.suffix} />
                </span>
                <p className="mt-3 max-w-[250px] text-sm leading-relaxed text-sand/60">
                  {d.l}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p
            className="mt-14 font-mono text-[10px] uppercase tracking-[0.2em] text-sand/35"
            data-testid="data-source"
          >
            {t.home.data.source}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function MissionVision() {
  const { t } = useLang();
  const blocks = [
    { ...t.home.mission, img: IMAGES.missionImg, testid: "mission-section" },
    { ...t.home.vision, img: IMAGES.visionImg, testid: "vision-section" },
  ];
  return (
    <section className="relative overflow-hidden bg-sand">
      <img
        src={IMAGES.venice}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.07]"
        data-testid="mission-bg"
      />
      <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        {blocks.map((b, bi) => (
          <div
            key={bi}
            className={`grid items-center gap-12 lg:grid-cols-12 ${
              bi > 0 ? "mt-24 lg:mt-32" : ""
            }`}
          >
            <div className={`lg:col-span-7 ${bi % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal>
                <p
                  className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-terra"
                  data-testid={`${b.testid}-eyebrow`}
                >
                  <span className="inline-block h-[2px] w-8 bg-terra" />
                  {b.eyebrow}
                </p>
                <h2
                  className="mt-5 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight text-night sm:text-4xl"
                  data-testid={b.testid}
                >
                  <WordReveal text={b.title} delay={0.1} />
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-mutedx md:text-lg">
                  {b.text}
                </p>
              </Reveal>
            </div>
            <div className={`lg:col-span-5 ${bi % 2 === 1 ? "lg:order-1" : ""}`}>
              <ParallaxImage
                src={b.img}
                className="aspect-[4/5] rounded-t-[10rem] rounded-b-2xl"
                hoverScale={1.2}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TwoLands() {
  const { t } = useLang();
  const cards = [
    { img: IMAGES.milan, caption: t.home.twoLands.captionIta },
    { img: IMAGES.casaBiz, caption: t.home.twoLands.captionMar },
  ];
  return (
    <section className="bg-night" data-testid="two-lands-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              {t.home.twoLands.eyebrow}
            </p>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-sand sm:text-4xl">
              <WordReveal text={t.home.twoLands.title} delay={0.1} />
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="text-base leading-relaxed text-sand/65">
              {t.home.twoLands.text}
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {cards.map((c, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <figure className="group relative">
                <ParallaxImage
                  src={c.img}
                  alt={c.caption}
                  className="aspect-[16/10] rounded-2xl"
                  hoverScale={1.2}
                />
                <figcaption className="pointer-events-none absolute bottom-5 left-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-sand">
                  <span className="inline-block h-[2px] w-6 bg-gold" />
                  {c.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { t } = useLang();
  return (
    <section className="bg-sand" data-testid="services-preview">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-terra">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              {t.home.servicesPreview.eyebrow}
            </p>
            <h2 className="mt-5 max-w-xl font-serif text-3xl font-medium leading-tight text-night sm:text-4xl">
              <WordReveal text={t.home.servicesPreview.title} delay={0.1} />
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/servizi"
              data-testid="services-preview-link"
              className="group inline-flex items-center gap-2 rounded-full border border-night/20 px-6 py-3 text-sm font-bold text-night transition-all duration-200 hover:-translate-y-0.5 hover:border-terra hover:text-terra"
            >
              {t.home.servicesPreview.link}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14">
          {t.home.servicesPreview.items.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <Link
                to="/servizi"
                data-testid={`services-preview-row-${i + 1}`}
                className="group relative grid items-center gap-3 border-t border-transparent py-7 transition-colors duration-300 hover:bg-sandcard md:grid-cols-12 md:gap-6 md:px-4"
              >
                <span className="absolute inset-x-0 top-0 h-px bg-linec" />
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                  className="absolute inset-x-0 top-0 h-[2px] origin-left bg-terra"
                />
                <span className="font-mono text-xs text-terra md:col-span-1">
                  0{i + 1}
                </span>
                <h3 className="font-serif text-xl font-medium text-night transition-colors duration-300 group-hover:text-terra md:col-span-5 md:text-2xl">
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed text-mutedx md:col-span-5">
                  {s.short}
                </p>
                <span className="hidden justify-self-end md:col-span-1 md:block">
                  <ArrowUpRight className="h-5 w-5 text-night/30 transition-all duration-300 group-hover:rotate-45 group-hover:text-terra" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamPreview() {
  const { t } = useLang();
  const names = ["Mohammed Hammouch", "Sana Ed Dami", "Wissam El Aissaoui"];
  return (
    <section className="border-t border-linec bg-sandcard" data-testid="team-preview">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-terra">
              <span className="inline-block h-[2px] w-8 bg-terra" />
              {t.home.teamPreview.eyebrow}
            </p>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-night sm:text-4xl">
              <WordReveal text={t.home.teamPreview.title} delay={0.1} />
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/team"
              data-testid="team-preview-link"
              className="group inline-flex items-center gap-2 rounded-full border border-night/20 px-6 py-3 text-sm font-bold text-night transition-all duration-200 hover:-translate-y-0.5 hover:border-terra hover:text-terra"
            >
              {t.home.teamPreview.link}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-12">
          <div className="relative lg:col-span-8" data-testid="team-group-photo">
            <ParallaxImage
              src="/team-group.webp"
              alt="Mohammed Hammouch, Sana Ed Dami e Wissam El Aissaoui — i tre soci di SWM Consulting"
              className="aspect-[16/9] rounded-2xl"
              hoverScale={1.1}
            />
            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-t from-night/75 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2.5">
              {names.map((n) => (
                <span
                  key={n}
                  className="rounded-full bg-night/60 px-4 py-2 text-xs font-bold text-sand backdrop-blur-md"
                >
                  {n}
                </span>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="lg:col-span-4">
            <p className="font-serif text-2xl italic leading-relaxed text-night md:text-3xl">
              {t.home.teamPreview.text}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-terra" data-testid="cta-band">
      <motion.img
        src={IMAGES.bridgeLight}
        alt=""
        aria-hidden="true"
        initial={{ scale: 1 }}
        whileInView={{ scale: 1.08 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 3, ease: EASE }}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        data-testid="cta-bg-image"
      />
      <div className="absolute inset-0 bg-terra/75" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />
      <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <Reveal>
          <h2
            className="max-w-2xl font-serif text-4xl font-medium leading-tight text-sand sm:text-4xl"
            data-testid="cta-band-title"
          >
            <WordReveal text={t.home.cta.title} delay={0.1} />
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-sand/85">
            {t.home.cta.text}
          </p>
          <Link
            to="/contatti"
            data-testid="cta-band-btn"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-night px-8 py-4 text-sm font-bold text-sand transition-all duration-200 hover:-translate-y-0.5 hover:text-gold"
          >
            {t.home.cta.btn}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <main>
      <KineticHero />
      <Stats />
      <MissionVision />
      <DataBand />
      <TwoLands />
      <ServicesPreview />
      <CorridorMap />
      <TeamPreview />
      <CtaBand />
    </main>
  );
}
